import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import { API_BASE_URL, FONT_UI } from "../layouts/constants";

function getToken() {
  return (
    localStorage.getItem("guardToken") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("adminToken")
  );
}

function isGuardSession() {
  return localStorage.getItem("isGuardLoggedIn") === "true";
}

export default function RegisteredDevicesPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [devices, setDevices] = useState([]);
  const [search, setSearch] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const errorShownRef = useRef(false);

  // Confirmation modal state
  const [confirmDelete, setConfirmDelete] = useState(null); // device object or null

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const isGuardLoggedIn = localStorage.getItem("isGuardLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const guardData = (() => {
      try {
        return JSON.parse(localStorage.getItem("guardData") || "{}");
      } catch {
        return {};
      }
    })();

    if (!token || (!isLoggedIn && !isGuardLoggedIn)) {
      navigate("/");
      return;
    }

    const local = isGuardSession()
      ? {
          company_name:
            guardData.fullName ||
            guardData.name ||
            guardData.fld_name ||
            localStorage.getItem("guardName") ||
            "Guard",
          licensedFor: "Guard Access",
          admin_active: 1,
          company_address: "",
          company_phone: "",
          admin_email:
            guardData.email ||
            guardData.fld_mail ||
            localStorage.getItem("guardEmail") ||
            "",
          company_logo: "",
          user_map: localStorage.getItem("user_map") || "",
        }
      : {
          company_name:
            localStorage.getItem("company_name") || userData.company_name || "",
          licensedFor:
            localStorage.getItem("licensedFor") || userData.licensedFor || "",
          admin_active: Number(
            localStorage.getItem("admin_active") ?? userData.admin_active ?? 0
          ),
          company_address:
            localStorage.getItem("company_address") ||
            userData.company_address ||
            "",
          company_phone:
            localStorage.getItem("company_phone") ||
            userData.company_phone ||
            "",
          admin_email:
            localStorage.getItem("admin_email") || userData.admin_email || "",
          company_logo:
            localStorage.getItem("company_logo") || userData.company_logo || "",
          user_map: localStorage.getItem("user_map") || userData.user_map || "",
        };

    setDashboardData(local);
    fetchDevices(token);
  }, [navigate]);

  const fetchDevices = async (token) => {
    try {
      setLoading(true);
      errorShownRef.current = false;

      const res = await fetch(`${API_BASE_URL}/device_register/registered`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await res.json();

      if (!res.ok || result.success === false) {
        throw new Error(result.message || "Failed to fetch registered devices");
      }

      const list = Array.isArray(result.data) ? result.data : [];
      setDevices(list);
      toast.success("Registered devices loaded successfully!");
    } catch (error) {
      console.error("Devices page error:", error);
      toast.error(error.message || "Failed to load registered devices.");
      errorShownRef.current = true;
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearch("");
  };

  const openDeleteConfirm = (device) => {
    setConfirmDelete(device);
  };

  const closeDeleteConfirm = () => {
    setConfirmDelete(null);
  };

  const confirmAndDelete = async () => {
    if (!confirmDelete) return;

    const token = getToken();
    const id = confirmDelete.id || confirmDelete.device_id;

    try {
      setDeletingId(id);

      const res = await fetch(`${API_BASE_URL}/device_register/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await res.json();

      if (!res.ok || result.success === false) {
        throw new Error(result.message || "Failed to delete device");
      }

      setDevices((prev) => prev.filter((d) => (d.id || d.device_id) !== id));
      toast.success("Device registration deleted successfully.");
      closeDeleteConfirm();
    } catch (error) {
      console.error("Delete device error:", error);
      toast.error(error.message || "Failed to delete device.");
    } finally {
      setDeletingId(null);
    }
  };

  const filteredDevices = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return devices;

    return devices.filter((d) => {
      return (
        String(d.guard_name || d.name || "").toLowerCase().includes(keyword) ||
        String(d.guard_id || d.user_id || "").toLowerCase().includes(keyword) ||
        String(d.device_model || d.model || "").toLowerCase().includes(keyword) ||
        String(d.device_id || d.imei || d.uuid || "").toLowerCase().includes(keyword) ||
        String(d.platform || d.os || "").toLowerCase().includes(keyword) ||
        String(d.app_version || "").toLowerCase().includes(keyword) ||
        String(d.phone || d.mobile || "").toLowerCase().includes(keyword)
      );
    });
  }, [devices, search]);

  const routePage = useMemo(
    () =>
      dashboardData.user_map === "google" ? "/route-master" : "/route-master1",
    [dashboardData.user_map]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading registered devices...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="registered-devices"
      activeMenu="registered-devices"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: { primary: "#22c55e", secondary: "white" },
          },
          error: {
            iconTheme: { primary: "#ef4444", secondary: "white" },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Registered App Devices
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          View and manage devices registered by guards. Delete only when the
          guard changes phone.
        </p>

        {/* Important notice */}
        <div className="mb-5 rounded-xl border border-amber-500/40 bg-amber-500/10 px-5 py-4">
          <p className="m-0 text-[13px] font-semibold text-amber-300">
            If the guard changes the phone, then only delete this data.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-[18px]">
          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--btn-bg)] border border-[var(--surface-border)] shadow-[0_4px_18px_var(--accent-glow)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--btn-text)] opacity-75 m-0 mb-1.5">
              Total Registered Devices
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--btn-text)]">
              {devices.length}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Showing
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {filteredDevices.length}
            </p>
          </div>
        </div>

        {/* Filter panel */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="flex justify-between items-center gap-4 flex-wrap mb-5">
            <div>
              <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Device List
              </h3>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Search by guard name, device model, IMEI / UUID, etc.
              </p>
            </div>

            <button
              type="button"
              onClick={handleClear}
              disabled={!search.trim()}
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              Clear
            </button>
          </div>

          <input
            type="text"
            placeholder="Search guard, device model, IMEI, platform..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
            style={{ fontFamily: FONT_UI }}
          />
        </div>

        {/* Device list */}
        {filteredDevices.length === 0 ? (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] text-center">
            <p className="text-[13px] text-[var(--text-muted)] m-0">
              No registered devices found.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filteredDevices.map((device) => {
              const id = device.id || device.device_id;
              const isDeleting = deletingId === id;

              return (
                <div
                  key={id}
                  className="bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-2xl p-5 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] transition-colors"
                >
                  <div className="flex justify-between gap-3 mb-4">
                    <div>
                      <h4 className="m-0 text-lg font-extrabold text-[var(--text-primary)]">
                        {device.guard_name || device.name || "Unknown Guard"}
                      </h4>
                      <p className="mt-1 text-sm text-[var(--text-muted)]">
                        Guard ID: {device.guard_id || device.user_id || "-"}
                      </p>
                    </div>
                    <span className="h-fit px-3 py-1.5 rounded-full text-xs font-extrabold bg-[rgba(59,130,246,0.12)] border border-[rgba(59,130,246,0.28)] text-[#3b82f6]">
                      {device.platform || device.os || "App"}
                    </span>
                  </div>

                  <div className="space-y-1.5 text-sm text-[var(--text-muted)] mb-5">
                    <p>
                      <b className="text-[var(--text-secondary)]">Device Model:</b>{" "}
                      {device.device_model || device.model || "-"}
                    </p>
                    <p>
                      <b className="text-[var(--text-secondary)]">Device ID / UUID:</b>{" "}
                      <span className="break-all">
                        {device.device_id || device.uuid || device.imei || "-"}
                      </span>
                    </p>
                    <p>
                      <b className="text-[var(--text-secondary)]">App Version:</b>{" "}
                      {device.app_version || "-"}
                    </p>
                    <p>
                      <b className="text-[var(--text-secondary)]">Phone / Mobile:</b>{" "}
                      {device.phone || device.mobile || "-"}
                    </p>
                    <p>
                      <b className="text-[var(--text-secondary)]">Registered At:</b>{" "}
                      {device.registered_at
                        ? new Date(device.registered_at).toLocaleString()
                        : device.created_at
                        ? new Date(device.created_at).toLocaleString()
                        : "-"}
                    </p>
                    <p>
                      <b className="text-[var(--text-secondary)]">Last Active:</b>{" "}
                      {device.last_active
                        ? new Date(device.last_active).toLocaleString()
                        : "-"}
                    </p>
                  </div>

                  <button
                    type="button"
                    disabled={isDeleting}
                    onClick={() => openDeleteConfirm(device)}
                    className="w-full border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    style={{
                      background: "rgba(239,68,68,0.15)",
                      color: "#ef4444",
                      border: "1px solid rgba(239,68,68,0.35)",
                    }}
                  >
                    {isDeleting ? "Deleting..." : "Delete Device"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-[var(--card-bg)] border border-[var(--card-border)] shadow-2xl overflow-hidden">
            <div className="px-6 pt-6 pb-4">
              <h3 className="text-lg font-extrabold text-[var(--text-primary)] m-0 mb-3">
                Confirm Delete
              </h3>

              {/* Important note above the main warning */}
              <div className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3">
                <p className="m-0 text-[13px] font-semibold text-amber-300">
                  If he change the phone then only delete this data.
                </p>
              </div>

              <p className="text-[14px] text-[var(--text-muted)] m-0 mb-2">
                You are about to delete the device registration for:
              </p>
              <p className="text-[15px] font-bold text-[var(--text-primary)] m-0 mb-4">
                {confirmDelete.guard_name || confirmDelete.name || "Unknown Guard"}
              </p>

              <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3">
                <p className="m-0 text-[13px] font-semibold text-red-300">
                  If you delete, the guard can’t login. He will want to register
                  again.
                </p>
              </div>
            </div>

            <div className="flex gap-3 px-6 pb-6">
              <button
                type="button"
                onClick={closeDeleteConfirm}
                className="flex-1 rounded-xl px-4 py-2.5 text-[13px] font-extrabold border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmAndDelete}
                disabled={!!deletingId}
                className="flex-1 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer disabled:opacity-60"
                style={{
                  background: "#ef4444",
                  color: "white",
                }}
              >
                {deletingId ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}